// Display text names the live-streaming unit "ULLFU" (Ultra Low Latency Forwarding Unit). Visible
// text only: code names (ULLFU*, sfu*), API paths and console logs still say ULLFU.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getNodeMonitoringSystem,
  getNodeMonitoringRecPlayback,
  getNodePipelines,
  getNodeUllfuSessions,
  getMonitoringLocalNodes,
  getNodeUptimeLog,
} from '../services/api.service';
import { PageTitle } from '@trinetra/shared';
import PerformanceTab from '../components/monitoring/PerformanceTab';
import HistoryTab from '../components/monitoring/HistoryTab';
import {
  BoltIcon,
  ChartBarIcon,
  VideoCameraIcon,
  ArrowsRightLeftIcon,
  CircleStackIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  UserIcon,
  FilmIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/solid';

const POLL_INTERVAL = 10_000;

/* ── formatters ─────────────────────────────────── */
function fmtUptime(sec: number) {
  if (!sec || sec < 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtBytes(b: number) {
  if (!b || b === 0) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtBps(b: number) {
  if (!b || b === 0) return '0 bps';
  const bits = b * 8;
  if (bits < 1000) return `${bits.toFixed(0)} bps`;
  if (bits < 1000000) return `${(bits / 1000).toFixed(1)} Kbps`;
  return `${(bits / 1000000).toFixed(2)} Mbps`;
}

function fmtRate(v: number) {
  if (!v || v === 0) return '0';
  if (v < 1000) return `${v.toFixed(0)}/s`;
  return `${(v / 1000).toFixed(1)}K/s`;
}

/* ── small reusable pieces ──────────────────────── */
function ProgressBar({ percent }: { percent: number }) {
  const cls = percent > 90 ? 'bg-red-500' : percent > 75 ? 'bg-amber-500' : percent > 60 ? 'bg-yellow-500' : 'bg-brand';
  return (
    <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
      <div className={`h-full rounded-full transition-all duration-500 ${cls}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

function SectionCard({ icon, title, children, actions }: { icon: string; title: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="rounded-lg shadow-card" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--foreground)' }}><span>{icon}</span>{title}</h3>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value, sub, accent = false }: { label: string; value: any; sub?: string; accent?: boolean }) {
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
      <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>{label}</div>
      <div className={`text-xl font-bold font-mono ${accent ? 'text-brand' : ''}`} style={accent ? {} : { color: 'var(--foreground)' }}>{value}</div>
      {sub && <div className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, valueColor }: { label: string; value: any; valueColor?: string }) {
  return (
    <div className="rounded-md px-3 py-2" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
      <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>{label}</div>
      <div className="text-sm font-mono font-semibold" style={{ color: valueColor || 'var(--foreground)' }}>{value ?? '—'}</div>
    </div>
  );
}

function NoData({ msg }: { msg?: string }) {
  return <div className="rounded-lg p-8 text-center text-sm" style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>{msg || 'No data available'}</div>;
}

/* ── Uptime Tab Component ────────────────────────── */
function UptimeTab({ data }: { data: any }) {
  if (!data || !data.entries || data.entries.length === 0) return <NoData msg="No uptime data recorded yet. Data will appear once the node goes online/offline." />;

  const { entries, summary } = data;

  const fmtDuration = (sec: number) => {
    if (!sec || sec <= 0) return '0s';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const fmtDate = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const totalSec = (summary?.total_online_sec || 0) + (summary?.total_offline_sec || 0);
  const uptimePct = totalSec > 0 ? ((summary.total_online_sec / totalSec) * 100).toFixed(2) : '—';

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="rounded-lg p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Uptime (30d)</div>
            <div className="text-2xl font-bold font-mono" style={{ color: '#10b981' }}>{uptimePct}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Total Online</div>
            <div className="text-sm font-mono font-semibold" style={{ color: '#10b981' }}>{fmtDuration(summary?.total_online_sec || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Total Offline</div>
            <div className="text-sm font-mono font-semibold" style={{ color: '#ef4444' }}>{fmtDuration(summary?.total_offline_sec || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Events</div>
            <div className="text-sm font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{summary?.entries || 0}</div>
          </div>
        </div>
      </div>

      {/* Visual History Bar */}
      <div className="rounded-lg p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="text-[10px] uppercase tracking-wider font-medium mb-2" style={{ color: 'var(--muted-foreground)' }}>Visual History</div>
        <div className="flex h-6 rounded overflow-hidden gap-px">
          {[...entries].reverse().slice(-30).map((entry: any, i: number) => {
            const isOnline = entry.status === 'online';
            const dur = entry.duration_seconds || 0;
            const minW = Math.max(dur > 3600 ? 3 : 1, Math.min(40, Math.round(dur / 600)));
            return (
              <div
                key={i}
                title={`${entry.status.toUpperCase()} — ${fmtDuration(dur)}\n${fmtDate(entry.started_at)}`}
                className="transition-all hover:opacity-80"
                style={{ background: isOnline ? '#10b981' : '#ef4444', flex: `${minW} 0 0`, minWidth: '3px', opacity: entry.active ? 1 : 0.7 }}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-1 text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>
          <span>Oldest</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#10b981' }} /> Online</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#ef4444' }} /> Offline</span>
          </span>
          <span>Now</span>
        </div>
      </div>

      {/* Availability Log Table */}
      <div className="rounded-lg overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
          <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Availability Log</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--input-background)' }}>
                <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--muted-foreground)' }}>#</th>
                <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--muted-foreground)' }}>Status</th>
                <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--muted-foreground)' }}>Interval</th>
                <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--muted-foreground)' }}>Duration</th>
                <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--muted-foreground)' }}>Activity</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry: any, i: number) => {
                const isOnline = entry.status === 'online';
                const num = entries.length - i;
                return (
                  <tr key={entry.id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2 font-mono" style={{ color: 'var(--muted-foreground)' }}>{num}</td>
                    <td className="px-3 py-2">
                      {entry.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: isOnline ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: isOnline ? '#10b981' : '#ef4444' }}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400 animate-pulse'}`} />
                          {isOnline ? 'ONLINE' : 'DOWN'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: isOnline ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: isOnline ? '#10b981' : '#ef4444' }}>
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                      <div>{fmtDate(entry.started_at)}</div>
                      {entry.active ? (
                        <div className="text-emerald-400 font-semibold">ACTIVE NOW</div>
                      ) : entry.ended_at ? (
                        <div>to {fmtDate(entry.ended_at)}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold" style={{ color: isOnline ? '#10b981' : '#ef4444' }}>
                      {fmtDuration(entry.duration_seconds || 0)}
                    </td>
                    <td className="px-3 py-2 text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                      {entry.reason || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TabLoader() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2" style={{ borderColor: 'var(--muted)', borderTopColor: 'var(--brand)' }} /></div>;
}

function StatusDot({ status, label }: { status: string; label: string }) {
  const color = status === 'running' ? 'bg-emerald-400' : status === 'error' ? 'bg-red-400' : 'bg-gray-400';
  return <span className="flex items-center gap-1 text-[10px] font-mono"><span className={`w-2 h-2 rounded-full ${color}`} />{label}</span>;
}

function PipelineStatusBadge({ active, status, uptime }: { active: boolean; status?: string; uptime?: number }) {
  if (!active) return <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
    <XCircleIcon className="w-3.5 h-3.5 opacity-50" /> off
  </span>;
  const running = status === 'running';
  // Transitional states (starting/connecting/waiting) are not failures → amber.
  const transitional = status === 'starting' || status === 'connecting' || status === 'waiting';
  const cls = running ? 'text-emerald-400' : transitional ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="inline-flex flex-col items-center gap-0.5">
      <span className={`inline-flex items-center gap-1 text-[10px] ${cls}`}>
        {running ? <CheckCircleSolidIcon className="w-3.5 h-3.5" /> : <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
        {status || 'on'}
      </span>
      {running && (uptime ?? 0) > 0 && (
        <span className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>up {fmtUptime(uptime ?? 0)}</span>
      )}
    </div>
  );
}

function Sparkline({ data, color, label, unit = '', maxOverride, showDate }: { data: any[]; color: string; label: string; unit?: string; maxOverride?: number; showDate?: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ idx: number; px: number } | null>(null);

  if (!data || data.length < 2) return <NoData msg={`No ${label} data yet`} />;
  const entries = data.map((d: any) => ({ t: d.ts ?? d.t ?? 0, v: typeof d === 'number' ? d : (d.value ?? d.v ?? 0) }));
  const vals = entries.map((e) => e.v);
  const min = Math.min(...vals);
  const max = maxOverride ?? Math.max(...vals);
  const range = max - min || 1;
  const w = 300, h = 80, pad = 2;

  const coords = entries.map((e, i) => ({
    x: pad + (i / (entries.length - 1)) * (w - 2 * pad),
    y: h - pad - ((e.v - min) / range) * (h - 2 * pad),
  }));
  const points = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const fillPoints = `${pad},${h - pad} ${points} ${w - pad},${h - pad}`;
  const last = vals[vals.length - 1];

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || entries.length < 2) return;
    const pxX = e.clientX - rect.left;
    const ratio = pxX / rect.width;
    const idx = Math.max(0, Math.min(entries.length - 1, Math.round(ratio * (entries.length - 1))));
    setHover({ idx, px: pxX });
  };

  const fmtTime = (ts: number) => {
    // ts might be seconds (Prometheus) or milliseconds — normalize
    const msTs = ts > 1e12 ? ts : ts * 1000;
    const d = new Date(msTs);
    const firstTs = entries[0].t;
    const lastTs = entries[entries.length - 1].t;
    const span = lastTs - firstTs;
    // If span > 24h (in either seconds or ms) OR showDate forced
    const spanIsDays = span > 86400 || span > 86400_000 || showDate;
    if (spanIsDays) {
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const hoverEntry = hover ? entries[hover.idx] : null;
  const hoverCoord = hover ? coords[hover.idx] : null;

  return (
    <div className="relative" ref={containerRef} onMouseMove={handleMouseMove} onMouseLeave={() => setHover(null)} style={{ cursor: 'crosshair' }}>
      {label && <div className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>{label}</div>}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: h }} preserveAspectRatio="none">
        <polygon points={fillPoints} fill={color} fillOpacity={0.15} />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        {hoverCoord && (
          <>
            <line x1={hoverCoord.x} y1={pad} x2={hoverCoord.x} y2={h - pad} stroke={color} strokeWidth="0.8" strokeDasharray="3,2" opacity="0.7" />
            <circle cx={hoverCoord.x} cy={hoverCoord.y} r="3" fill={color} stroke="#fff" strokeWidth="1" />
          </>
        )}
      </svg>
      {hover && hoverEntry && (
        <div
          className="absolute z-10 px-2.5 py-1.5 rounded-md shadow-lg pointer-events-none text-[10px] font-mono"
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
            left: Math.min(hover.px, (containerRef.current?.offsetWidth || 200) - 120),
            top: 16,
            minWidth: 100,
          }}
        >
          <div style={{ color }}>{hoverEntry.v.toFixed(2)}{unit}</div>
          <div style={{ color: 'var(--muted-foreground)' }}>{fmtTime(hoverEntry.t)}</div>
        </div>
      )}
      <div className="flex justify-between text-[9px] font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
        <span>{entries.length > 0 ? fmtTime(entries[0].t) : ''}</span>
        <span style={{ color }}>Current: {last.toFixed(1)}{unit}</span>
        <span>{entries.length > 0 ? fmtTime(entries[entries.length - 1].t) : ''}</span>
      </div>
    </div>
  );
}

function TempGauge({ label, value }: { label: string; value: number }) {
  const color = value > 85 ? '#ef4444' : value > 70 ? '#f59e0b' : '#10b981';
  return (
    <div className="rounded-lg p-3 text-center" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
      <div className="text-[9px] uppercase tracking-wider font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>{label}</div>
      <div className="text-2xl font-bold font-mono" style={{ color }}>{value > 0 ? `${value.toFixed(0)}°C` : '—'}</div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   Main Component — Node Monitor Page
   Shows monitoring for a specific local node via tunnel proxy
   ════════════════════════════════════════════════════ */
export default function NodeMonitorPage() {
  const { nodeId } = useParams<{ nodeId: string }>();
  const navigate = useNavigate();
  const [nodeName, setNodeName] = useState<string>('');
  const [nodeStatus, setNodeStatus] = useState<string>('');
  const [activeTab, setActiveTab] = useState('performance');
  const [error, setError] = useState<string | null>(null);

  // Per-tab data + loading
  const [perfData, setPerfData] = useState<any>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [pipelineData, setPipelineData] = useState<any>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [ullfuData, setUllfuData] = useState<any>(null);
  const [ullfuLoading, setUllfuLoading] = useState(false);
  const [recPlayData, setRecPlayData] = useState<any>(null);
  const [recPlayLoading, setRecPlayLoading] = useState(false);
  const [recPlayPage, setRecPlayPage] = useState(1);
  const [uptimeData, setUptimeData] = useState<any>(null);
  const [uptimeLoading, setUptimeLoading] = useState(false);
  const timerRef = useRef<number | null>(null);

  /**
   * The site in the path can now change under the page: the title bar's switcher re-points it
   * (see ScopeSync). Every fetch below keys off `nodeId` and re-runs on its own, but the numbers
   * already on screen belong to the site just left — and a poll interval of stale CPU, GPU and
   * pipeline figures sitting under the new site's name is worse than an honest loader.
   */
  useEffect(() => {
    setNodeName('');
    setNodeStatus('');
    setPerfData(null);
    setPipelineData(null);
    setUllfuData(null);
    setRecPlayData(null);
    setRecPlayPage(1);
    setUptimeData(null);
    setError(null);
  }, [nodeId]);

  // Fetch node info on mount
  useEffect(() => {
    (async () => {
      try {
        const nodes = await getMonitoringLocalNodes();
        const node = nodes?.nodes?.find((n: any) => n.nodeId === nodeId);
        if (node) {
          setNodeName(node.name);
          setNodeStatus(node.status);
        }
      } catch { /* ignore */ }
    })();
  }, [nodeId]);

  const fetchPerformance = useCallback(async (loader = false) => {
    if (!nodeId) return;
    if (loader) setPerfLoading(true);
    try { setPerfData(await getNodeMonitoringSystem(nodeId)); setError(null); }
    catch (e: any) { setError(e?.response?.data?.error || e.message); }
    finally { setPerfLoading(false); }
  }, [nodeId]);

  const fetchPipelines = useCallback(async (loader = false) => {
    if (!nodeId) return;
    if (loader) setPipelineLoading(true);
    try { setPipelineData(await getNodePipelines(nodeId)); }
    catch (e) { console.warn('[NodeMonitor] pipelines:', e); }
    finally { setPipelineLoading(false); }
  }, [nodeId]);

  const fetchUllfu = useCallback(async (loader = false) => {
    if (!nodeId) return;
    if (loader) setUllfuLoading(true);
    try { setUllfuData(await getNodeUllfuSessions(nodeId)); }
    catch (e) { console.warn('[NodeMonitor] sfu:', e); }
    finally { setUllfuLoading(false); }
  }, [nodeId]);

  const fetchRecPlayback = useCallback(async (loader = false) => {
    if (!nodeId) return;
    if (loader) setRecPlayLoading(true);
    try { setRecPlayData(await getNodeMonitoringRecPlayback(nodeId)); }
    catch (e) { console.warn('[NodeMonitor] rec:', e); }
    finally { setRecPlayLoading(false); }
  }, [nodeId]);

  const fetchUptime = useCallback(async (loader = false) => {
    if (!nodeId) return;
    if (loader) setUptimeLoading(true);
    try { setUptimeData(await getNodeUptimeLog(nodeId)); }
    catch (e) { console.warn('[NodeMonitor] uptime:', e); }
    finally { setUptimeLoading(false); }
  }, [nodeId]);

  const fetchForTab = useCallback((tab: string, loader = true) => {
    switch (tab) {
      case 'performance': return fetchPerformance(loader);
      case 'history': return; // HistoryTab manages its own fetching
      case 'pipelines': return fetchPipelines(loader);
      case 'sfu': return fetchUllfu(loader);
      case 'recording-playback': return fetchRecPlayback(loader);
      case 'uptime': return fetchUptime(loader);
    }
  }, [fetchPerformance, fetchPipelines, fetchUllfu, fetchRecPlayback, fetchUptime]);

  useEffect(() => {
    fetchForTab(activeTab, true);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => fetchForTab(activeTab, false), POLL_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeTab, fetchForTab]);

  const cameras = pipelineData?.cameras || [];

  const tabs = [
    { id: 'performance', label: 'Performance', icon: BoltIcon },
    { id: 'history', label: 'History', icon: ChartBarIcon },
    { id: 'pipelines', label: 'Pipelines', icon: VideoCameraIcon },
    { id: 'sfu', label: 'ULLFU', icon: ArrowsRightLeftIcon },
    { id: 'recording-playback', label: 'Rec & Play', icon: CircleStackIcon },
    { id: 'uptime', label: 'Uptime', icon: CheckCircleIcon },
  ];

  return (
    <div className="space-y-3">
      <PageTitle title={nodeName || 'Node Monitor'} subtitle={`Remote monitoring via cloud tunnel — ${nodeId}`}>
        {nodeStatus && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
            nodeStatus === 'online' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${nodeStatus === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
            {nodeStatus}
          </span>
        )}
        <button onClick={() => navigate('/platform-health')}
          className="inline-flex h-8 items-center gap-1 px-2.5 rounded-md text-xs font-medium transition-colors"
          style={{ background: 'var(--input-background)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
          <ArrowLeftIcon className="h-3.5 w-3.5" /> Back
        </button>
      </PageTitle>

      {error && (
        <div className="rounded-lg p-3 text-sm" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: 'var(--destructive)' }}>
          ⚠ {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-0.5 -mb-px scrollbar-hide" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0"
            style={{ borderBottom: activeTab === t.id ? '2px solid var(--brand)' : '2px solid transparent', color: activeTab === t.id ? 'var(--brand)' : 'var(--muted-foreground)' }}>
            <t.icon className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">{t.label}</span>
            <span className="sm:hidden">{t.label.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* ═══ TAB: Performance ═══ */}
      {activeTab === 'performance' && (perfLoading && !perfData ? <TabLoader /> :
        <PerformanceTab data={perfData} />
      )}

      {/* ═══ TAB: Performance History ═══ */}
      {activeTab === 'history' && (
        <HistoryTab nodeId={nodeId!} />
      )}

      {/* ═══ TAB: Pipeline Monitoring ═══ */}
      {activeTab === 'pipelines' && (pipelineLoading && !pipelineData ? <TabLoader /> :
        <div className="space-y-3">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Cameras" value={cameras.length} accent />
            <StatCard label="Recording Pipelines" value={pipelineData?.recording_pipelines ?? 0} />
            <StatCard label="Live Pipelines" value={pipelineData?.live_pipelines ?? 0} />
            <StatCard label="WebRTC Sessions" value={pipelineData?.total_webrtc_sessions ?? 0} />
          </div>

          {/* Service reachability */}
          <div className="grid grid-cols-3 gap-3">
            {[{ label: 'Recording Service', online: pipelineData?.recording_service_reachable }, { label: 'Live Ingest', online: pipelineData?.live_ingest_reachable }, { label: 'ULLFU Service', online: pipelineData?.live_service_reachable }].map(svc => (
              <div key={svc.label} className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                <div className={`w-2 h-2 rounded-full ${svc.online ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>{svc.label}</div>
                  <div className="text-xs font-medium" style={{ color: svc.online ? '#10b981' : '#ef4444' }}>{svc.online ? 'Online' : 'Offline'}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Per-camera pipeline table */}
          <div className="rounded-lg" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="px-4 py-3 text-sm font-semibold flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', color: 'var(--foreground)' }}>
              <span>Per-Camera Pipeline Status</span>
              <span className="text-[10px] font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--input-background)', color: 'var(--muted-foreground)' }}>
                {cameras.length} cameras
              </span>
            </div>
            {cameras.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Camera</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Pipeline</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Status</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Codec</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Resolution</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Transcode</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Viewers</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cameras.map((cam: any) => {
                      const rd = cam.recording_details || {};
                      const ld = cam.live_details || {};
                      const hasRec = cam.recording_pipeline;
                      const hasLive = cam.live_pipeline;
                      const rows: { key: string; type: string; status?: string; codec?: string; resolution?: string; isTranscoding?: boolean; mode?: string; lastError?: string; restarts: number; uptime?: number }[] = [];
                      if (hasRec) {
                        const recRes = rd.resolution_output && rd.resolution_output !== rd.resolution_source
                          ? `${rd.resolution_source || '—'} → ${rd.resolution_output}` : (rd.resolution_source || '—');
                        rows.push({ key: cam.id + '-rec', type: 'rec', status: rd.status || rd.pipeline_status, codec: rd.codec, resolution: recRes, isTranscoding: rd.is_transcoding, mode: rd.mode, lastError: rd.last_error, restarts: rd.restart_count || 0, uptime: rd.uptime_seconds || 0 });
                      }
                      if (hasLive) {
                        const liveRes = ld.resolution_output && ld.resolution_output !== ld.resolution_source
                          ? `${ld.resolution_source || '—'} → ${ld.resolution_output}` : (ld.resolution_source || '—');
                        rows.push({ key: cam.id + '-live', type: 'live', status: ld.pipeline_status, codec: ld.codec, resolution: liveRes, isTranscoding: ld.is_transcoding, mode: ld.mode, lastError: ld.last_error, restarts: ld.restart_count || 0, uptime: ld.uptime_seconds || 0 });
                      }
                      if (rows.length === 0) rows.push({ key: cam.id + '-off', type: 'none', status: 'off', restarts: 0 });

                      return rows.map((row, ri) => (
                        <tr key={row.key} style={{ borderBottom: ri === rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          {ri === 0 && (
                            <td className="px-3 py-2 align-top" rowSpan={rows.length}>
                              <div className="font-medium" style={{ color: 'var(--foreground)' }}>{cam.name}</div>
                              <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{cam.id?.slice(0, 12)}…</div>
                            </td>
                          )}
                          <td className="px-3 py-1.5 text-center">
                            {row.type === 'rec' ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>⏺ REC</span>
                            ) : row.type === 'live' ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: 'rgba(16,185,129,0.08)', color: '#34d399' }}>▶ LIVE</span>
                            ) : <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>—</span>}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {row.type !== 'none' ? <PipelineStatusBadge active={true} status={row.status} uptime={row.uptime} /> : <PipelineStatusBadge active={false} />}
                          </td>
                          <td className="px-3 py-1.5 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>{row.codec || '—'}</td>
                          <td className="px-3 py-1.5 text-center font-mono text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{row.resolution || '—'}</td>
                          <td className="px-3 py-1.5 text-center">
                            {row.isTranscoding ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>{(row.mode || 'cpu').toUpperCase()}</span>
                            ) : row.type !== 'none' ? <span style={{ color: 'var(--muted-foreground)' }}>passthru</span> : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                          </td>
                          {ri === 0 && (
                            <td className="px-3 py-1.5 text-center font-mono align-top" rowSpan={rows.length} style={{ color: cam.viewer_count > 0 ? '#3b82f6' : 'var(--muted-foreground)' }}>{cam.viewer_count || 0}</td>
                          )}
                          <td className="px-3 py-1.5 text-center">
                            {(() => {
                              // status !== 'running' => the error is live; otherwise it's
                              // the reason for an earlier restart that has since recovered.
                              const activeProblem = row.status && row.status !== 'running' && row.status !== 'off';
                              if (row.lastError) {
                                return (
                                  <span title={activeProblem ? row.lastError : `Recovered — last incident: ${row.lastError}`} className="inline-flex items-center gap-1 cursor-help">
                                    <ExclamationTriangleIcon className={`w-3.5 h-3.5 ${activeProblem ? 'text-red-400' : 'text-amber-400/60'}`} />
                                    {row.restarts > 0 && <span className="text-[9px] text-amber-400">{row.restarts}×</span>}
                                  </span>
                                );
                              }
                              if (row.restarts > 0) {
                                return <span className="font-mono text-[10px]" style={{ color: '#f59e0b' }}>{row.restarts}× restart{activeProblem ? '' : ', recovered'}</span>;
                              }
                              return <span style={{ color: 'var(--muted-foreground)' }}>—</span>;
                            })()}
                          </td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>No cameras found</div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB: ULLFU Monitoring ═══ */}
      {activeTab === 'sfu' && (ullfuLoading && !ullfuData ? <TabLoader /> :
        (() => {
          const sessions = ullfuData?.sessions || [];
          const ullfuCameras = ullfuData?.cameras || [];
          const bw = ullfuData?.bandwidth || {};
          return (
        <div className="space-y-3">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard label="Connected Sessions" value={ullfuData?.connected_sessions ?? 0} accent />
            <StatCard label="Total Sessions" value={ullfuData?.total_sessions ?? 0} />
            <StatCard label="Active Cameras" value={ullfuData?.active_cameras ?? 0} />
            <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>
                <ArrowUpIcon className="w-3 h-3 text-emerald-400" />TX Bandwidth
              </div>
              <div className="text-lg font-bold font-mono" style={{ color: 'var(--foreground)' }}>{bw.estimated_outbound_mbps ?? 0} Mbps</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>
                <ArrowDownIcon className="w-3 h-3 text-blue-400" />RX Bitrate
              </div>
              <div className="text-lg font-bold font-mono" style={{ color: 'var(--foreground)' }}>{((bw.total_bitrate_in_kbps || 0) / 1000).toFixed(2)} Mbps</div>
            </div>
          </div>

          {/* Health */}
          {ullfuData?.health && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[{ l: 'Cameras Total', v: ullfuData.health.cameras_total }, { l: 'Cameras Active', v: ullfuData.health.cameras_active }, { l: 'Viewers', v: ullfuData.health.viewers }, { l: 'Tracks Served', v: ullfuData.health.tracks_served }].map(m => (
                <div key={m.l} className="rounded-lg p-2" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                  <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>{m.l}</div>
                  <div className="text-sm font-bold font-mono" style={{ color: 'var(--foreground)' }}>{m.v ?? 0}</div>
                </div>
              ))}
            </div>
          )}

          {/* Per-session table */}
          <div className="rounded-lg" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="px-4 py-3 text-sm font-semibold flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', color: 'var(--foreground)' }}>
              <SignalIcon className="w-4 h-4 text-blue-400" />
              <span>Viewer Sessions</span>
              <span className="text-[10px] font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--input-background)', color: 'var(--muted-foreground)' }}>
                {sessions.length} sessions
              </span>
            </div>
            {sessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['User / Session', 'State', 'Tracks', 'TX (Mbps)', 'RX (bytes)', 'NACK', 'PLI', 'Uptime'].map((h, i) => (
                        <th key={h} className={`${i === 0 ? 'text-left' : 'text-center'} px-3 py-2 text-[10px] uppercase tracking-wider font-medium`} style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s: any) => (
                      <tr key={s.session_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <UserIcon className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
                            <div>
                              <div className="font-medium" style={{ color: 'var(--foreground)' }}>{s.user_name || s.user_email || 'Unknown'}</div>
                              <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>
                                {s.session_id?.slice(0, 10)}… | {s.remote_addr || '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            s.state === 'connected' ? 'text-emerald-400' : s.state === 'failed' ? 'text-red-400' : 'text-amber-400'
                          }`} style={{ background: s.state === 'connected' ? 'rgba(16,185,129,0.1)' : s.state === 'failed' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)' }}>
                            <span className={`w-1.5 h-1.5 rounded-full ${s.state === 'connected' ? 'bg-emerald-400' : s.state === 'failed' ? 'bg-red-400' : 'bg-amber-400'}`} />
                            {s.state}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--foreground)' }}>
                          <span title={`Live: ${s.live_tracks}, Playback: ${s.playback_tracks}, Paused: ${s.paused_tracks}`}>
                            {s.total_tracks} <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>({s.live_tracks}L/{s.playback_tracks}P)</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: (s.bandwidth_mbps || 0) > 0 ? '#10b981' : 'var(--muted-foreground)' }}>
                          {s.bandwidth_mbps?.toFixed(2) || '0.00'}
                          <div className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>{fmtBytes(s.bytes_sent)}</div>
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>
                          {fmtBytes(s.bytes_received || 0)}
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: (s.nack_count || 0) > 0 ? '#f59e0b' : 'var(--muted-foreground)' }}>{s.nack_count || 0}</td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: (s.pli_count || 0) > 0 ? '#f59e0b' : 'var(--muted-foreground)' }}>{s.pli_count || 0}</td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>{fmtUptime(s.uptime_seconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>No active viewer sessions</div>
            )}
          </div>

          {/* Camera streams table */}
          {ullfuCameras.length > 0 && (
            <div className="rounded-lg" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="px-4 py-3 text-sm font-semibold flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <span>Camera Ingest Streams</span>
                <span className="text-[10px] font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--input-background)', color: 'var(--muted-foreground)' }}>
                  {ullfuCameras.length} streams
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Camera', 'Status', 'RTP Port', 'Bitrate (kbps)', 'Packets Recv', 'Subscribers'].map((h, i) => (
                        <th key={h} className={`${i === 0 ? 'text-left' : 'text-center'} px-3 py-2 text-[10px] uppercase tracking-wider font-medium`} style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ullfuCameras.map((c: any, i: number) => (
                      <tr key={c.camera_id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="px-3 py-2">
                          <div className="font-medium" style={{ color: 'var(--foreground)' }}>{c.camera_name || c.camera_id?.slice(0, 12) || `Stream ${i+1}`}</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            c.status === 'active' && c.bitrate_kbps > 0 ? 'text-emerald-400' : 'text-amber-400'
                          }`} style={{ background: c.status === 'active' && c.bitrate_kbps > 0 ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)' }}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>{c.rtp_port || '—'}</td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: c.bitrate_kbps > 0 ? '#10b981' : 'var(--muted-foreground)' }}>{c.bitrate_kbps || 0}</td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>{c.packets_received?.toLocaleString() || 0}</td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: c.subscriber_count > 0 ? '#3b82f6' : 'var(--muted-foreground)' }}>{c.subscriber_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          );
        })()      
      )}

      {/* ═══ TAB: Recording & Playback ═══ */}
      {activeTab === 'recording-playback' && (recPlayLoading && !recPlayData ? <TabLoader /> :
        (() => {
          if (!recPlayData) return <NoData msg="Recording & playback metrics unavailable" />;
          const { hls, recording, storage, max_storage_gb } = recPlayData;
          const storageArr = Array.isArray(storage) ? storage : [];
          const totalDiskUsed = storageArr.reduce((sum: number, s: any) => sum + (Number(s.total_size) || 0), 0);
          const totalSegments = storageArr.reduce((sum: number, s: any) => sum + (Number(s.segment_count) || 0), 0);
          const totalCameras = storageArr.length;
          const PAGE_SIZE = 10;
          const resolutionMap: Record<string, string> = {};
          if (recording && Array.isArray(recording.cameras)) {
            for (const cam of recording.cameras) {
              if (cam.camera_id) resolutionMap[cam.camera_id] = cam.resolution_source || cam.resolution_output || cam.resolution || '';
            }
          }
          const totalPages = Math.max(1, Math.ceil(storageArr.length / PAGE_SIZE));
          const currentPage = Math.min(recPlayPage, totalPages);
          const pagedStorage = storageArr.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

          return (
        <div className="space-y-3">
          {/* Overall disk usage */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>
                <CircleStackIcon className="w-3.5 h-3.5 text-emerald-400" />Total Disk Used
              </div>
              <div className="text-lg font-bold font-mono text-emerald-400">{fmtBytes(totalDiskUsed)}</div>
            </div>
            <StatCard label="Total Chunks" value={totalSegments.toLocaleString()} />
            <StatCard label="Cameras Recording" value={totalCameras} />
            <StatCard label="Max Storage" value={max_storage_gb ? `${max_storage_gb} GB` : '∞'} />
          </div>

          {/* Storage usage bar */}
          {max_storage_gb > 0 && (
            <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
              <div className="flex justify-between text-[10px] mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                <span>Disk Usage</span>
                <span>{fmtBytes(totalDiskUsed)} / {max_storage_gb} GB ({((totalDiskUsed / (max_storage_gb * 1073741824)) * 100).toFixed(1)}%)</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div className="h-full rounded-full transition-all" style={{
                  width: `${Math.min(100, (totalDiskUsed / (max_storage_gb * 1073741824)) * 100)}%`,
                  background: (totalDiskUsed / (max_storage_gb * 1073741824)) > 0.9 ? '#ef4444' : (totalDiskUsed / (max_storage_gb * 1073741824)) > 0.7 ? '#f59e0b' : '#10b981',
                }} />
              </div>
            </div>
          )}

          {/* Per-camera storage table with pagination */}
          <div className="rounded-lg" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="px-4 py-3 text-sm font-semibold flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', color: 'var(--foreground)' }}>
              <FilmIcon className="w-4 h-4 text-red-400" />
              <span>Per-Camera Recording Storage</span>
              <span className="text-[10px] font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--input-background)', color: 'var(--muted-foreground)' }}>
                {storageArr.length} cameras
              </span>
            </div>
            {storageArr.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Camera', 'Codec', 'Resolution', 'Total Chunks', 'Last Chunk Size', 'Total Space'].map((h, i) => (
                          <th key={h} className={`${i === 0 ? 'text-left' : 'text-center'} px-3 py-2 text-[10px] uppercase tracking-wider font-medium`} style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedStorage.map((s: any, i: number) => (
                        <tr key={s.camera_id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td className="px-3 py-2">
                            <div className="font-medium" style={{ color: 'var(--foreground)' }}>{s.camera_name || 'Unknown'}</div>
                            <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{s.camera_id?.slice(0, 12)}…</div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium font-mono" style={{
                              background: (s.codec || '').includes('265') ? 'rgba(139,92,246,0.1)' : 'rgba(59,130,246,0.1)',
                              color: (s.codec || '').includes('265') ? '#8b5cf6' : '#3b82f6',
                            }}>
                              {s.codec || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{resolutionMap[s.camera_id] || '—'}</td>
                          <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--foreground)' }}>{Number(s.segment_count || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--muted-foreground)' }}>{fmtBytes(Number(s.latest_segment_size) || 0)}</td>
                          <td className="px-3 py-2 text-center font-mono font-medium" style={{ color: 'var(--foreground)' }}>{fmtBytes(Number(s.total_size) || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2" style={{ borderTop: '1px solid var(--border)' }}>
                    <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => setRecPlayPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                        className="px-2 py-1 rounded text-[10px] disabled:opacity-30" style={{ background: 'var(--input-background)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
                        ← Prev
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let p: number;
                        if (totalPages <= 5) p = i + 1;
                        else if (currentPage <= 3) p = i + 1;
                        else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
                        else p = currentPage - 2 + i;
                        return (
                          <button key={p} onClick={() => setRecPlayPage(p)}
                            className="w-6 h-6 rounded text-[10px] font-medium" style={{
                              background: p === currentPage ? 'var(--brand)' : 'var(--input-background)',
                              color: p === currentPage ? '#fff' : 'var(--foreground)',
                              border: '1px solid var(--border)',
                            }}>
                            {p}
                          </button>
                        );
                      })}
                      <button onClick={() => setRecPlayPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                        className="px-2 py-1 rounded text-[10px] disabled:opacity-30" style={{ background: 'var(--input-background)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>No per-camera storage data</div>
            )}
          </div>
        </div>
          );
        })()
      )}

      {/* ═══ TAB: Uptime ═══ */}
      {activeTab === 'uptime' && (uptimeLoading && !uptimeData ? <TabLoader /> :
        <UptimeTab data={uptimeData} />
      )}

      <div className="text-[10px] text-center font-mono" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }}>
        Auto-refreshing every {POLL_INTERVAL / 1000}s • Data via cloud tunnel
      </div>
    </div>
  );
}
